import { redirect } from '@tanstack/react-router';
import { Col, Row } from 'antd';
import React from 'react';

import { useLang } from '@/i18n/use-lang.ts';
import { Authentication } from '@/rbac';
import { useSupervisorStats } from '@/routes/dashboard/hooks';
import {
  CallsVolumeChart,
  HandledRejectedRate,
  InboundCallStatistics,
  KpiCards,
  OperatorStatusPanel,
} from '@/routes/dashboard/statistics';
import { Header } from '@/routes/monitor/components/Header.tsx';
import { useUserStore } from '@/stores/user.store.ts';
import { cn } from '@/utils/cn';

import GlobalCallQueue from './components/GlobalCallQueue';
import OperatorListPanel from './components/OperatorListPanel';

const monitorLangs = {
  'en-GB': {
    kpi_totalCallsToday: 'Total Calls\ntoday',
    kpi_totalCallsLastHour: 'Total calls last\nhour',
    kpi_callsInProgress: 'Number of calls\nin progress',
    kpi_avgResponseTime: 'Average\nResponse Time',
    kpi_liveGlobalQueueLength: 'Live global\nqueue length',
    inbound_answerTime60s: 'Answer-time\n≤ 60s',
    inbound_answerTime180s: 'Answer-time\n≤ 180s',
    inbound_missedRateToday: 'Missed Rate\nToday',
  },
  'zh-CN': {
    kpi_totalCallsToday: '今日总通话',
    kpi_totalCallsLastHour: '过去一小时\n总通话',
    kpi_callsInProgress: '进行中通话数',
    kpi_avgResponseTime: '平均\n响应时间',
    kpi_liveGlobalQueueLength: '全局队列\n实时长度',
    inbound_answerTime60s: '60秒内\n接听率',
    inbound_answerTime180s: '180秒内\n接听率',
    inbound_missedRateToday: '今日\n未接率',
  },
  'cy-GB': {
    kpi_totalCallsToday: 'Cyfanswm galwadau\nheddiw',
    kpi_totalCallsLastHour: 'Cyfanswm galwadau\nyr awr ddiwethaf',
    kpi_callsInProgress: 'Galwadau ar y gweill',
    kpi_avgResponseTime: 'Amser ymateb\ncymedrig',
    kpi_liveGlobalQueueLength: 'Hyd ciw byw',
    inbound_answerTime60s: 'Amser ateb ≤60s',
    inbound_answerTime180s: 'Amser ateb ≤180s',
    inbound_missedRateToday: 'Cyfradd methu heddiw',
  },
};

const MONITOR_VARS = {
  '--stats-card-bg': '#ffffff',
  '--stats-card-border': '#e5e7eb',
  '--stats-text': '#1f2937',
  '--stats-text-secondary': '#6b7280',
  '--stats-bar-track': '#f3f4f6',
} as React.CSSProperties;

export const Route = createFileRoute({
  component: MonitorPage,
  beforeLoad: async () => {
    // check user auth
    const user = useUserStore.getState().user;
    if (!user?.accessToken) {
      return redirect({
        to: '/auth/sign-in',
        search: { redirect: '/monitor' },
      });
    }
  },
});

function MonitorPage() {
  const l = useLang(monitorLangs);
  const {
    metrics,
    operators,
    kpiItems,
    operatorStatuses,
    inboundStatsItems,
    personalQueues,
    globalQueueCalls,
    operatorTotal,
  } = useSupervisorStats({ operatorSort: true });

  const kpiItemsWithLabels = React.useMemo(
    () =>
      kpiItems.map((i) => ({
        ...i,
        label: l(i.labelKey as keyof (typeof monitorLangs)['en-GB']),
      })),
    [kpiItems, l],
  );
  const inboundStatsItemsWithLabels = React.useMemo(
    () =>
      inboundStatsItems.map((i) => ({
        ...i,
        label: l(i.labelKey as keyof (typeof monitorLangs)['en-GB']),
      })),
    [inboundStatsItems, l],
  );

  return (
    <Authentication require={'arc:supervisor:*'} failMode={'empty'}>
      <div
        style={MONITOR_VARS}
        className={cn('monitor flex h-dvh w-screen flex-col overflow-auto')}
      >
        <Header />
        <div className={'h-full min-h-0 flex-1 flex-col p-2.5'}>
          <Row className={'h-full flex-1'} gutter={10}>
            <Col span={5} className={'h-full overflow-auto'}>
              <OperatorListPanel
                operators={operators?.operators ?? []}
                personalQueues={personalQueues}
                total={operatorTotal}
              />
            </Col>
            <Col span={14}>
              <div className={'flex h-full flex-col gap-2.5'}>
                <div>
                  <KpiCards items={kpiItemsWithLabels} />
                </div>
                <CallsVolumeChart
                  inbound={metrics?.volumes.inbound ?? []}
                  outbound={metrics?.volumes.outbound ?? []}
                  className={'flex-1'}
                />
                <Row gutter={10}>
                  <Col span={12}>
                    <OperatorStatusPanel statuses={operatorStatuses} />
                  </Col>
                  <Col span={12}>
                    <div className="flex flex-1 flex-col justify-between gap-4">
                      <InboundCallStatistics
                        items={inboundStatsItemsWithLabels}
                        className="flex-1"
                      />
                      <HandledRejectedRate
                        handledRate={metrics?.today.rates.handled_rate ?? 0}
                        rejectedRate={metrics?.today.rates.rejected_rate ?? 0}
                        className="shrink-0"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col span={5}>
              <GlobalCallQueue calls={globalQueueCalls} />
            </Col>
          </Row>
        </div>
      </div>
    </Authentication>
  );
}
